import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';

/*
 * Glowora: scroll-fix voor de cart drawer.
 * 1. Eén scrollcontainer: alleen .cart-drawer__content scrollt. De productlijst
 *    scrollt niet meer apart, zodat hij nooit tot 0 hoogte kan krimpen en er
 *    geen geneste scrollers zijn die op iOS vastlopen.
 * 2. Tijdens een update zette het thema pointer-events: none op de hele cart,
 *    inclusief de scrollcontainers. Nu worden alleen de productregels en de
 *    afrekenknop geblokkeerd; scrollen blijft altijd werken.
 * 3. De scroll-hint vervaging valt weg, anders vervaagt de afrekenknop onderaan.
 */
(function addCartDrawerScrollFix() {
  if (document.getElementById('glw-cart-drawer-scroll-fix')) return;
  const style = document.createElement('style');
  style.id = 'glw-cart-drawer-scroll-fix';
  style.textContent = `
    .cart-drawer__dialog .cart-drawer__content {
      overflow-y: auto !important;
      overflow-x: hidden;
      overscroll-behavior: contain;
      -webkit-overflow-scrolling: touch;
      mask-image: none !important;
      -webkit-mask-image: none !important;
    }
    .cart-drawer__dialog .cart-drawer__items {
      overflow: visible !important;
      flex: 0 0 auto;
      mask-image: none !important;
      -webkit-mask-image: none !important;
    }
    .cart-drawer .cart-items-component.cart-items-disabled {
      pointer-events: auto;
    }
    .cart-drawer .cart-items-disabled .cart-items__table,
    .cart-drawer .cart-items-disabled .cart__ctas {
      pointer-events: none;
    }
  `;
  document.head.appendChild(style);
})();

/**
 * A custom element that manages a cart drawer.
 *
 * Glowora: de sticky-status van de samenvatting werd alleen bij openen berekend.
 * Na een wijziging (aantal, verwijderen, cadeau erbij, upsell geladen) bleef de
 * oude status staan. Dan kromp de productlijst tot bijna 0 hoogte en kon je niet
 * meer scrollen. Nu wordt de status opnieuw berekend na elke cart-update, bij
 * resize en zodra de samenvatting van hoogte verandert.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {Element | null} */
  #observedSummary = null;

  /** @type {number | null} */
  #rafId = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartUpdate, this.#scheduleStickyUpdate);
    this.addEventListener(DialogOpenEvent.eventName, this.#scheduleStickyUpdate);
    window.addEventListener('resize', this.#scheduleStickyUpdate);

    if (typeof ResizeObserver !== 'undefined') {
      this.#resizeObserver = new ResizeObserver(this.#scheduleStickyUpdate);
      const { dialog } = /** @type {Refs} */ (this.refs);
      if (dialog) this.#resizeObserver.observe(dialog);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#scheduleStickyUpdate);
    this.removeEventListener(DialogOpenEvent.eventName, this.#scheduleStickyUpdate);
    window.removeEventListener('resize', this.#scheduleStickyUpdate);

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#observedSummary = null;

    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }

  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open') && !event?.detail?.data?.deferDrawerOpen) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  /**
   * Bundles all triggers into one measurement per frame. The cart-update event fires
   * before the drawer is morphed, so measuring in the next frame sees the new markup.
   */
  #scheduleStickyUpdate = () => {
    if (this.#rafId !== null) return;

    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#updateStickyState();
    });
  };

  /**
   * Watches the current summary element; after an empty cart it is a new element.
   * @param {Element | null} summary
   */
  #observeSummary(summary) {
    if (!this.#resizeObserver || summary === this.#observedSummary) return;

    if (this.#observedSummary) this.#resizeObserver.unobserve(this.#observedSummary);
    if (summary) this.#resizeObserver.observe(summary);
    this.#observedSummary = summary;
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    this.#observeSummary(summary);

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;

    // Closed drawer: nothing to measure. The open event measures again.
    if (!dialog.open || drawerHeight <= 0) return;

    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    const sticky = ratio > this.#summaryThreshold ? 'false' : 'true';

    if (dialog.getAttribute('cart-summary-sticky') !== sticky) {
      dialog.setAttribute('cart-summary-sticky', sticky);
    }
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
